<template>
  <PageTemplate>

    <BackButton
        :label="$t('general.button.back')"
        :to="{ name: 'addressBookCompanyList' }"
        marginBottom="5"/>

    <div class="d-flex justify-content-between">
      <h6 class="element-header">
        {{ t('general.contacts') }}
      </h6>
    </div>

    <div class="element-box">
      <h5 class="form-header mb-4">
        {{ t('address_book.company') }}
      </h5>

      <form @submit.prevent="submitForm" novalidate>
        <div class="row">
          <div class="col-lg-6">
            <BaseInput v-model="formData.firstName" :label="t('general.first_name')" type="text"></BaseInput>
            <BaseInput v-model="formData.lastName" :label="t('general.last_name')" type="text"></BaseInput>
            <BaseInput v-model="formData.company" :label="t('general.company_client')" type="text"></BaseInput>
            <BaseInput v-model="formData.phoneNumber" :label="t('general.phone_number')" type="text"></BaseInput>
            <BaseInput
                v-model="formData.email"
                :validation="v$.email"
                :messages="customMessages.email.invalid"
                :label="t('general.email')" type="email">
            </BaseInput>
          </div>
          <div class="col-lg-6">
            <BaseInput v-model="formData.address" :label="t('general.street')" type="text"></BaseInput>
            <BaseInput v-model="formData.zip" :label="t('general.postal_code')" type="text"></BaseInput>
            <BaseInput v-model="formData.city" :label="t('general.city')" type="text"></BaseInput>
            <BaseInput v-model="formData.position" :label="t('general.position')" type="text"></BaseInput>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <div>
              <TextEditor
                  v-model="formData.info"
                  @update:model-value="formData.info = $event"
                  :inputStyle="{ height: '200px'}"
                  :label="t('general.info')"/>
            </div>
          </div>
        </div>
        <div class="form-buttons-w text-center">
          <SaveButton
              :label="t('general.button.save')"
              :loading="isSubmitting"/>
        </div>
      </form>
    </div>
  </PageTemplate>
</template>

<script setup lang="ts">
import companyService from '@/api/AddressBook/company';
import BaseInput from '@/components/base/BaseInput.vue';
import CreateButton from '@/components/CreateButton.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import TextEditor from '@/components/TextEditor.vue';
import {CloseToast, ErrorToast, SuccessToast} from '@/components/VueToast';
import router from '@/router';
import useVuelidate from '@vuelidate/core';
import {email, helpers} from '@vuelidate/validators';
import {computed, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import {IAddressBook} from "@/interfaces/AddressBook/IAddressBook";
import BackButton from "@/components/button/BackButton.vue";
import SaveButton from "@/components/button/SaveButton.vue";

const {t} = useI18n();
const route = useRoute();
const companyId = route.params.id;
const isSubmitting = ref(false);

const formData = ref <IAddressBook>({
  firstName: '',
  lastName: '',
  company: '',
  phoneNumber: '',
  email: '',
  address: '',
  zip: '',
  city: '',
  position: '',
  info: '',
});

// VALIDATION
const customMessages = {
  email: {
    invalid: t('form_validation.invalid_email'),
  },
};

const rules = computed(() => {
  return {
    email: {
      valid: helpers.withMessage(customMessages.email.invalid, email),
    },
  };
});

const v$ = useVuelidate(rules, formData);

async function getCompany() {
  try {
    const {data} = await companyService.getCompany(+companyId);
    formData.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

const submitForm = async () => {
  v$.value.$touch(); // Trigger validation
  if (v$.value.$invalid) {
    return; // Prevent submission if the form is invalid
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {

    companyId ?
        await companyService.putCompany(formData.value, +companyId) :
        await companyService.postCompany(formData.value);

    await router.push({name: 'addressBookCompanyList'});
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(async () => {
  CloseToast()
  if (companyId) {
    await getCompany();
  }
});

</script>

<style scoped>

</style>